// @flow
import * as React from 'react';
import { forwardRef, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  ButtonProps,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack
} from '@mui/material';
import { ModalCloseButton } from '@coverright/ui/shared';
import { Pen } from '@phosphor-icons/react';
import {
  useAffiliationPartnershipsQuery,
  useSaveChannelAttributionMutation
} from '../../../../../shared';
import { useClient } from '../../../hooks/client-context';
import { getUserName } from '@coverright/utils';
import { TextInput } from '@coverright/ui/inputs';
import _ from 'lodash';
import { LoadingButton } from '@mui/lab';
import { AffiliationPartnershipOutput } from '@coverright/data-access/types/enrollment';

type Props = {
  onSubmit: () => void
};
export const ChannelAttributionForm = (props: Props) => {
  const client = useClient();
  const [source, setSource] = React.useState<AffiliationPartnershipOutput | null>(client.utmSource && client.utmCampaign ? {
    source: client.utmSource,
    campaign: client.utmCampaign
  } : null);
  const [save, {loading: saveLoading}] = useSaveChannelAttributionMutation();
  const {data, loading} = useAffiliationPartnershipsQuery();

  const partnerships = useMemo(() => data?.affiliationPartnerships || [], [data?.affiliationPartnerships]);

  const sources = _.orderBy(_.uniqBy(partnerships, 'source'), 'source');

  const submit = async () => {
    await save({
      variables: {
        clientId: client.id,
        marketingSource: source?.source || null,
        marketingCampaign: source?.campaign || null
      }
    })
    props.onSubmit();
  }

  return (
    <Stack gap={1}>
      <Autocomplete fullWidth
                    sx={{mb: 1}}
                    loading={loading}
                    options={sources}
                    value={source}
                    filterOptions={(options, state) => (
                      options.filter(o => o.source.includes(state.inputValue))
                    )}
                    getOptionLabel={o => o.source}
                    onChange={(event, value) => {
                      setSource(value);
                    }}
                    clearOnBlur
                    renderInput={(params) => <TextInput
                      hideTick
                      label={'Search source'}
                      {...params}
                    />} />

      <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'flex-end'}>
        <Button onClick={() => props.onSubmit()} variant={'outlined'}>
          Cancel
        </Button>
        <LoadingButton
          loading={saveLoading}
          onClick={submit}
          variant={'contained'}
        >
          Save
        </LoadingButton>
      </Stack>
    </Stack>
  );
};

export const ChannelAttributionEditButton = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const [open, setOpen] = useState(false);

  return <>
    <Dialog fullWidth maxWidth={'xs'} open={open} onClose={() => setOpen(false)}>
      <DialogTitle variant={'h3'}>
        Channel attribution
        <ModalCloseButton onClick={() => setOpen(false)} sx={{position: 'absolute', top: 4, right: 8}} />
      </DialogTitle>
      <DialogContent>
        <ChannelAttributionForm onSubmit={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
    <IconButton ref={ref} size={'small'} {...props} onClick={e => {
      props.onClick && props.onClick(e)
      setOpen(true)
    }}>
      <Pen color="#a6a6a6" />
    </IconButton>
  </>
})
